import React, { useEffect, useRef, useState } from 'react';

interface VisualBreakProps {
  image: string;
  statement: string;
  subtext?: string;
  height?: 'sm' | 'md' | 'lg';
}

export const VisualBreak: React.FC<VisualBreakProps> = ({ 
  image, 
  statement, 
  subtext,
  height = 'md' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const heights = {
    sm: 'min-h-[200px] md:min-h-[250px]',
    md: 'min-h-[280px] md:min-h-[350px]',
    lg: 'min-h-[350px] md:min-h-[450px]',
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, []);

  return (
    <div 
      ref={ref}
      className={`relative ${heights[height]} flex items-center justify-center overflow-hidden`}
    >
      <div className="absolute inset-0">
        <img 
          src={image} 
          alt="" 
          className={`w-full h-full object-cover transition-transform duration-[2000ms] ${isVisible ? 'scale-100' : 'scale-110'}`}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-slate-900/75"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/30 to-transparent"></div>
      </div>
      <div className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <p className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {statement}
        </p>
        {subtext && (
          <p className="text-slate-300 text-sm md:text-base mt-3 max-w-2xl mx-auto">{subtext}</p>
        )}
      </div>
    </div>
  );
};
